import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Stack,
  Grid
} from '@mui/material';
import {
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Thermostat as ThermostatIcon,
  LocalPharmacy as ToxicityIcon,
  Speed as DifficultyIcon
} from '@mui/icons-material';

// Рендер блока «Рекомендации по уходу» (температура, вредители/болезни).
// Полив не дублируем: описание и автоматизация полива показаны в секции «Уход и автоматизация».
const renderRecommendations = (plantInfo) => {
  if (!plantInfo) return 'Нет рекомендаций';

  // Собираем рекомендации
  const recommendations = [];

  // Добавляем информацию о температуре
  if (plantInfo.care_info && plantInfo.care_info.temperature) {
    const temp = plantInfo.care_info.temperature;
    if (temp.optimal) {
      recommendations.push(
        <Box key="temp" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Температура:
          </Typography>
          <Typography variant="body2">
            {temp.optimal}
          </Typography>
        </Box>
      );
    }
  }

  // Добавляем информацию о болезнях/вредителях
  if (plantInfo.pests_and_diseases && plantInfo.pests_and_diseases.detected) {
    const pests = plantInfo.pests_and_diseases;

    if (pests.common_pests && pests.common_pests.length > 0) {
      recommendations.push(
        <Box key="pests" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
            Вредители:
          </Typography>
          <Typography variant="body2">
            {pests.common_pests.map((pest) => (typeof pest === 'string' ? pest : pest.name)).filter(Boolean).join(', ')}
          </Typography>
        </Box>
      );
    }

    if (pests.common_diseases && pests.common_diseases.length > 0) {
      recommendations.push(
        <Box key="diseases" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
            Болезни:
          </Typography>
          <Typography variant="body2">
            {pests.common_diseases.map((disease) => (typeof disease === 'string' ? disease : disease.name)).filter(Boolean).join(', ')}
          </Typography>
        </Box>
      );
    }
  }

  return recommendations.length > 0 ? (
    <Card variant="outlined" sx={{ boxShadow: 'none' }}>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        {recommendations}
      </CardContent>
    </Card>
  ) : (
    'Нет рекомендаций'
  );
};

// Подробные детали результата сканирования растения.
// Используется в раскрывающейся строке истории сканирований и на странице
// тестирования нейросети (ответ /admin/neuro/test-scan).
// Props:
//   plantInfo           — объект plant_info результата сканирования
//   recommendedProducts — массив рекомендованных товаров (опционально; в деталях
//                         пока не отображается — товары выводятся отдельной колонкой)
const ScanResultDetails = ({ plantInfo, recommendedProducts }) => {
  // prop зарезервирован для прямой передачи данных ответа test-scan
  void recommendedProducts;

  if (!plantInfo) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
        {plantInfo.name} {plantInfo.latin_name && plantInfo.latin_name !== 'data_not_available' && (
          <Typography component="span" variant="subtitle2" color="text.secondary">
            ({plantInfo.latin_name})
          </Typography>
        )}
      </Typography>

      {/* Основные характеристики */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DifficultyIcon color="primary" />
                Общие характеристики
              </Typography>

              <Stack spacing={1}>
                {plantInfo.difficulty_level && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Сложность ухода:</Typography>
                    <Chip
                      label={plantInfo.difficulty_level === 'easy' ? 'Легкий' :
                        plantInfo.difficulty_level === 'medium' ? 'Средний' : 'Сложный'}
                      color={plantInfo.difficulty_level === 'easy' ? 'success' :
                        plantInfo.difficulty_level === 'medium' ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Состояние растения:</Typography>
                  <Chip
                    label={plantInfo.is_healthy ? 'Здоровое' : 'Есть проблемы'}
                    color={plantInfo.is_healthy ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                {plantInfo.toxicity && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Токсичность:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip
                        icon={<ToxicityIcon />}
                        label={plantInfo.toxicity.toxicity_level === 'non_toxic' ? 'Безопасно' :
                          plantInfo.toxicity.toxicity_level === 'mildly_toxic' ? 'Слабо токсично' :
                            plantInfo.toxicity.toxicity_level === 'toxic' ? 'Токсично' :
                              plantInfo.toxicity.toxicity_level === 'highly_toxic' ? 'Очень токсично' :
                                'Неизвестно'}
                        color={plantInfo.toxicity.toxicity_level === 'non_toxic' ? 'success' : 'warning'}
                        size="small"
                      />
                      {plantInfo.toxicity.toxic_to_pets !== null && (
                        <Chip
                          label={plantInfo.toxicity.toxic_to_pets ? '❌ Опасно для животных' : '✅ Безопасно для животных'}
                          color={plantInfo.toxicity.toxic_to_pets ? 'error' : 'success'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {plantInfo.toxicity.toxic_to_children !== null && (
                        <Chip
                          label={plantInfo.toxicity.toxic_to_children ? '❌ Опасно для детей' : '✅ Безопасно для детей'}
                          color={plantInfo.toxicity.toxic_to_children ? 'error' : 'success'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ThermostatIcon color="secondary" />
                Условия содержания
              </Typography>

              {plantInfo.growing_conditions && (
                <Stack spacing={2}>
                  {plantInfo.growing_conditions.temperature && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>🌡️ Температура:</Typography>
                      <Typography variant="body2">
                        Оптимальная: {plantInfo.growing_conditions.temperature.optimal_min || plantInfo.growing_conditions.temperature.min}°-
                        {plantInfo.growing_conditions.temperature.optimal_max || plantInfo.growing_conditions.temperature.max}°C
                      </Typography>
                      {plantInfo.growing_conditions.temperature.winter_min && (
                        <Typography variant="caption" color="text.secondary">
                          Зимой не ниже: {plantInfo.growing_conditions.temperature.winter_min}°C
                        </Typography>
                      )}
                    </Box>
                  )}

                  {plantInfo.growing_conditions.lighting && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>☀️ Освещение:</Typography>
                      <Typography variant="body2">
                        {plantInfo.growing_conditions.lighting.type === 'direct_sun' ? 'Прямое солнце' :
                          plantInfo.growing_conditions.lighting.type === 'bright_indirect' ? 'Яркий рассеянный свет' :
                            plantInfo.growing_conditions.lighting.type === 'medium_light' ? 'Умеренное освещение' :
                              plantInfo.growing_conditions.lighting.type === 'low_light' ? 'Слабое освещение' :
                                plantInfo.growing_conditions.lighting.type}
                      </Typography>
                      {plantInfo.growing_conditions.lighting.hours_per_day && (
                        <Typography variant="caption" color="text.secondary">
                          {plantInfo.growing_conditions.lighting.hours_per_day} часов в день
                        </Typography>
                      )}
                    </Box>
                  )}

                  {plantInfo.growing_conditions.humidity && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>💧 Влажность:</Typography>
                      <Typography variant="body2">
                        {plantInfo.growing_conditions.humidity.optimal_percentage ||
                          `${plantInfo.growing_conditions.humidity.min_percentage}-${plantInfo.growing_conditions.humidity.max_percentage}`}%
                      </Typography>
                      {plantInfo.growing_conditions.humidity.misting_required && (
                        <Typography variant="caption" color="primary.main">
                          Требуется опрыскивание
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Уход и автоматизация */}
      {plantInfo.care_info && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" />
              Уход и автоматизация
            </Typography>

            <Grid container spacing={2}>
              {plantInfo.care_info.watering && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      💧 Полив
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {plantInfo.care_info.watering.description}
                    </Typography>
                    {plantInfo.care_info.watering.automation && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Автоматизация:</Typography>
                        <Typography variant="caption" display="block">
                          • Каждые {plantInfo.care_info.watering.automation.interval_days || '?'} дня
                        </Typography>
                        <Typography variant="caption" display="block">
                          • Время: {plantInfo.care_info.watering.automation.time_of_day === 'morning' ? 'Утром' :
                            plantInfo.care_info.watering.automation.time_of_day === 'evening' ? 'Вечером' : 'Любое время'}
                        </Typography>
                        {plantInfo.care_info.watering.automation.amount && plantInfo.care_info.watering.automation.amount !== 'data_not_available' && (
                          <Typography variant="caption" display="block">
                            • Количество: {plantInfo.care_info.watering.automation.amount}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {plantInfo.care_info.fertilizing && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(46, 125, 50, 0.04)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      🌱 Подкормка
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {plantInfo.care_info.fertilizing.description}
                    </Typography>
                    {plantInfo.care_info.fertilizing.automation && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Автоматизация:</Typography>
                        <Typography variant="caption" display="block">
                          • Каждые {plantInfo.care_info.fertilizing.automation.interval_days || '?'} дней
                        </Typography>
                        {plantInfo.care_info.fertilizing.automation.fertilizer_type && plantInfo.care_info.fertilizing.automation.fertilizer_type !== 'data_not_available' && (
                          <Typography variant="caption" display="block">
                            • Тип: {plantInfo.care_info.fertilizing.automation.fertilizer_type}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Описание */}
      {plantInfo.description && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Описание</Typography>
            <Typography variant="body2">{plantInfo.description}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Проблемы */}
      {plantInfo.pests_and_diseases?.common_problems && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              Анализ проблем растения
            </Typography>

            <Grid container spacing={1}>
              {Object.entries(plantInfo.pests_and_diseases.common_problems).map(([problem, data]) => (
                <Grid item xs={12} sm={6} md={4} key={problem}>
                  <Box
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: data.detected ? 'error.main' : 'grey.300',
                      bgcolor: data.detected ? 'rgba(211, 47, 47, 0.04)' : 'grey.50',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 'bold',
                      color: data.detected ? 'error.main' : 'text.secondary'
                    }}>
                      {data.detected ? '🔴' : '🟢'} {
                        problem === 'yellow_leaves' ? 'Желтые листья' :
                          problem === 'brown_leaf_tips' ? 'Коричневые кончики' :
                            problem === 'dropping_leaves' ? 'Опадение листьев' :
                              problem === 'slow_growth' ? 'Медленный рост' :
                                problem === 'wilting' ? 'Увядание' : problem
                      }
                    </Typography>
                    {data.detected && data.causes && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Причины: {data.causes.join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Рекомендации по уходу */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" gutterBottom>Рекомендации по уходу</Typography>
        {renderRecommendations(plantInfo)}
      </Box>
    </Box>
  );
};

export default ScanResultDetails;
